import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  MousePointer2,
  Move3D,
  Route,
  ZoomIn,
  ChevronRight,
  ChevronLeft,
  Compass,
  Play
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const onboardingSteps = [
  {
    icon: MousePointer2,
    title: "Click to Move",
    description: "Click anywhere on the floor or highlighted circles to move to that location. The view will smoothly transition to your new position.",
    tip: "Look for the circular markers on the ground - these are navigation points."
  },
  {
    icon: Move3D,
    title: "Look Around",
    description: "Click and drag to look around in any direction. On mobile, use one finger to pan the view.",
    tip: "Hold down the mouse button and drag to rotate your view 360°."
  },
  {
    icon: ZoomIn,
    title: "Zoom In & Out",
    description: "Use your scroll wheel to zoom in for details or zoom out for a wider view. On mobile, pinch to zoom.",
    tip: "Zoom in to inspect equipment details or read labels."
  },
  {
    icon: Route,
    title: "Guided Tours",
    description: "Click the tour icon in the top-right corner to start a guided tour. The tour will automatically take you through key locations with descriptions.",
    tip: "Guided tours are a great way to get familiar with the space."
  },
  {
    icon: Compass,
    title: "View Modes",
    description: "Switch between Inside View (walk through), Dollhouse View (see the whole building), and Floor Plan View using the buttons in the top-right.",
    tip: "Dollhouse view is perfect for understanding the overall layout."
  }
];

export function OnboardingScreen({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const { completeOnboarding } = useAuth();

  const handleComplete = () => {
    completeOnboarding();
    onComplete();
  };

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const step = onboardingSteps[currentStep];
  const Icon = step.icon;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="h-16 border-b border-border bg-card/50 backdrop-blur-sm px-4 flex items-center">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_7a888182-d9ad-4911-af25-412e3d52e004/artifacts/sstxximv_Zentara%20As-Ltd._-01.png" 
            alt="Zentara" 
            className="h-10 w-auto object-contain"
          />
          <div>
            <h1 className="text-lg font-semibold text-foreground leading-tight zentara-header">
              Zentara
            </h1>
            <p className="text-[10px] text-muted-foreground">
              Industrial & Commercial Asset Management
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-2xl w-full">
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {onboardingSteps.map((_, idx) => (
              <div
                key={idx}
                className={`h-2 rounded-full transition-all duration-300 ${
                  idx === currentStep 
                    ? 'w-8 bg-primary' 
                    : idx < currentStep 
                      ? 'w-2 bg-primary/50' 
                      : 'w-2 bg-muted'
                }`}
              />
            ))}
          </div>

          {/* Step content */}
          <div className="text-center">
            <Badge variant="outline" className="mb-6 text-xs border-primary/30 text-primary">
              Getting Started - Step {currentStep + 1} of {onboardingSteps.length}
            </Badge>

            <div className="w-24 h-24 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mx-auto mb-6">
              <Icon className="h-12 w-12" />
            </div>

            <h2 className="text-3xl font-bold text-foreground mb-4 zentara-header">
              {step.title}
            </h2>

            <p className="text-lg text-muted-foreground mb-6 max-w-md mx-auto">
              {step.description}
            </p>

            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 max-w-md mx-auto mb-8">
              <p className="text-sm text-primary">
                💡 <strong>Tip:</strong> {step.tip}
              </p>
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-muted-foreground"
            >
              Skip Tutorial
            </Button>

            <div className="flex items-center gap-3">
              {currentStep > 0 && (
                <Button variant="outline" onClick={handlePrev}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              )}
              
              <Button onClick={handleNext} className="gap-2">
                {currentStep === onboardingSteps.length - 1 ? (
                  <>
                    <Play className="h-4 w-4" />
                    Start Exploring
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default OnboardingScreen;
